import { useEffect, useState } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Copy, DeviceMobile, Sparkle, Info, Eye, EyeSlash } from "@phosphor-icons/react";

export default function SiriShortcutsPanel() {
  const [meta, setMeta] = useState(null);
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    api.get("/auth/api-token").then((r) => setMeta(r.data));
  }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/api-token");
      setToken(r.data.token);
      setShown(true);
      setMeta({ issued: true, preview: r.data.token.slice(0, 8) + "…" + r.data.token.slice(-6) });
      toast.success("Token API généré — copiez-le, il ne sera plus affiché");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copié`);
    } catch {
      toast.error("Copie impossible");
    }
  };

  const baseUrl = API;
  const examples = [
    { label: "Créer chantier", path: "/shortcuts/create-chantier", body: `{"name":"Nouveau chantier","client_name":"Client X","address":"Adresse"}` },
    { label: "Ajouter tâche rapide", path: "/shortcuts/quick-task", body: `{"label":"Prévoir échelle"}` },
    { label: "Signaler devis", path: "/shortcuts/flag-quote", body: `{"note":"Ajout clim salon","estimated_amount":1800}` },
    { label: "Chantier en cours", path: "/shortcuts/latest-chantier", body: "(GET, no body)" },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-2xl bg-gradient-to-br from-purple-500/15 to-pink-500/10 border border-purple-500/30 p-4 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-purple-500 flex items-center justify-center">
          <Sparkle size={22} weight="fill" color="white" />
        </div>
        <div className="flex-1">
          <div className="font-display font-semibold">Raccourcis Siri</div>
          <div className="text-xs text-white/70">Créez des chantiers, tâches et devis à la voix ou en 1 tap depuis l&apos;iPhone</div>
        </div>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-white/50">Votre token API personnel</div>
        {!meta?.issued && !token && (
          <Button onClick={generate} disabled={busy} className="w-full h-12 bg-purple-500 hover:bg-purple-600 rounded-xl" data-testid="siri-generate">
            {busy ? "…" : "Générer mon token API (1 an)"}
          </Button>
        )}
        {(token || meta?.preview) && (
          <div className="rounded-xl bg-black/40 border border-white/10 p-3 font-mono text-xs break-all" data-testid="siri-token">
            {token && shown ? token : (meta?.preview || "•••")}
          </div>
        )}
        {token && (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setShown((s) => !s)} className="flex-1 border border-white/10 h-9 text-xs">
              {shown ? <><EyeSlash size={12} /> Masquer</> : <><Eye size={12} /> Afficher</>}
            </Button>
            <Button onClick={() => copy(token, "Token")} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC] h-9 text-xs" data-testid="siri-copy-token">
              <Copy size={12} weight="bold" /> Copier
            </Button>
          </div>
        )}
        {meta?.issued && !token && (
          <Button onClick={generate} disabled={busy} variant="ghost" className="w-full border border-white/10 h-9 text-xs">
            Regénérer un nouveau token (invalide l&apos;ancien)
          </Button>
        )}
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-white/50 flex items-center gap-1.5">
          <DeviceMobile size={12} /> Configuration iPhone
        </div>
        <ol className="space-y-2 text-xs text-white/70 list-decimal list-inside">
          <li>Ouvrez l&apos;app <b>Raccourcis</b> sur iPhone → <b>+</b> nouveau raccourci</li>
          <li>Ajoutez l&apos;action <b>« Obtenir contenu de l&apos;URL »</b></li>
          <li>URL&nbsp;: collez l&apos;endpoint (ex&nbsp;: <span className="font-mono text-[#005CFF]">{baseUrl}/shortcuts/create-chantier</span>)</li>
          <li>Méthode&nbsp;: <b>POST</b> · En-têtes&nbsp;: <span className="font-mono text-[10px]">Authorization: Bearer &lt;votre token&gt;</span></li>
          <li>Corps requête&nbsp;: <b>JSON</b> avec les champs (ex&nbsp;: <span className="font-mono text-[10px]">name, client_name, address</span>)</li>
          <li>Nommez le raccourci <b>« Nouveau chantier BKPRO »</b> et activez <b>« Ajouter à Siri »</b> avec la phrase de votre choix</li>
        </ol>
      </div>

      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-2">
        <div className="text-[11px] uppercase tracking-widest text-white/50">Endpoints Siri disponibles</div>
        {examples.map((ex) => (
          <div key={ex.path} className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-1" data-testid={`siri-ex-${ex.path.split('/').pop()}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium">{ex.label}</div>
              <button onClick={() => copy(`${baseUrl}${ex.path}`, "URL")} className="text-[10px] uppercase tracking-widest text-[#005CFF] flex items-center gap-1">
                <Copy size={10} /> URL
              </button>
            </div>
            <div className="font-mono text-[10px] text-white/70 break-all">{baseUrl}{ex.path}</div>
            <div className="font-mono text-[10px] text-white/50 break-all">{ex.body}</div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-3 flex gap-2 text-xs text-amber-200">
        <Info size={14} weight="duotone" className="shrink-0 mt-0.5" />
        <div>Ne partagez jamais votre token API. Il donne accès à votre compte BKPRO pendant 1 an. En cas de perte, cliquez sur « Regénérer ».</div>
      </div>
    </div>
  );
}
